import {
  Alert,
  Anchor,
  Badge,
  Button,
  Code,
  Group,
  List,
  Stack,
  Text,
  Textarea,
  TextInput,
  Title,
} from "@mantine/core";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";

import {
  checkRuleConflicts,
  listRules,
  submitRule,
  validateRule,
  type RuleConflict,
  type RuleConflictsResponse,
  type RuleValidation,
  type SubmitResult,
} from "@/api/authoring";
import { useResolvedPermissions } from "@/auth/permissions";
import { useTenantScope } from "@/auth/tenant";
import { AppLayout } from "@/components/AppLayout";
import { t } from "@/i18n";
import { safeHttpUrl } from "@/util";

/**
 * Rule detail + draft editor (phase2-admin-ui slice).
 *
 * Shows one rule from the effective set as editable JSON. Validate runs the
 * backend cross-checks (biomarker refs, collision, content presence) without
 * writing. Submit opens an MR — org-layer only (the id must be
 * `{org}:#<digits>` per ADR-0008); base-corpus rules are read-only here and a
 * base rule is edited by saving a copy under the org namespace. The simulator
 * page (linked) runs any draft against the live engine.
 *
 * The editor, Validate and "Submit as MR" render only with `catalog:author`
 * (docs#239 item 8). A lab admin reads rules through `catalog:read`, and the submit
 * route refuses her server-side, so an editor she could not save from read as a
 * capability she does not have. Without it the rule shows as read-only JSON.
 */
export function RuleDetailPage(): JSX.Element {
  const { region = "EU", insightId = "" } = useParams();
  const [params] = useSearchParams();
  // A ClientAdmin can only ever browse/submit within their own org.
  const { org, pinned } = useTenantScope(params.get("org") ?? "");
  const permissions = useResolvedPermissions();
  const canAuthor =
    permissions.isReady && !permissions.isError && permissions.has("catalog:author");

  const { data, isPending, isError, error } = useQuery({
    queryKey: ["authoring-rules", region, org],
    queryFn: () => listRules(region, org || undefined),
  });
  const rule = data?.rules.find((r) => r.insight === insightId);

  const [draft, setDraft] = useState("");
  const [submitOrgInput, setSubmitOrgInput] = useState(org);
  const submitOrg = pinned ? org : submitOrgInput;
  useEffect(() => {
    if (rule !== undefined) setDraft(JSON.stringify(rule, null, 2));
  }, [rule]);

  const parseDraft = (): Record<string, unknown> | null => {
    try {
      return JSON.parse(draft) as Record<string, unknown>;
    } catch {
      return null;
    }
  };

  const validation = useMutation<RuleValidation, Error, Record<string, unknown>>({
    mutationFn: (parsed) => validateRule(parsed, region, org || undefined),
  });
  const submission = useMutation<SubmitResult, Error, Record<string, unknown>>({
    mutationFn: (parsed) => submitRule(submitOrg, parsed, region),
  });
  const conflicts = useMutation<RuleConflictsResponse, Error, Record<string, unknown>>({
    mutationFn: (parsed) => checkRuleConflicts(parsed, region, org || undefined),
  });

  const parsed = parseDraft();
  const draftId = typeof parsed?.insight === "string" ? parsed.insight : "";
  const isOrgNamespaced = submitOrg !== "" && draftId.startsWith(`${submitOrg}:#`);

  return (
    <AppLayout
      breadcrumbs={[
        { label: t("authoring.rules_title"), to: `/authoring/rules?region=${region}` },
        { label: insightId },
      ]}
    >
      <Title order={1}>{insightId}</Title>
      {isPending ? (
        <Text c="dimmed">{t("authoring.rules_loading")}</Text>
      ) : isError ? (
        <Alert color="red" title={t("authoring.error_title")}>
          {error instanceof Error ? error.message : t("authoring.error_generic")}
        </Alert>
      ) : rule === undefined ? (
        <Alert color="yellow" title={t("authoring.rule_not_found")}>
          {t("authoring.rule_not_found_body")}
        </Alert>
      ) : (
        <Stack mt="md">
          <Group>
            <Badge variant="light">{rule.type}</Badge>
            <Text size="sm">
              {t("authoring.col_priority")}: {rule.priority}
            </Text>
            <Text size="sm">
              {t("authoring.col_biomarkers")}: {rule.related_biomarkers.join(", ")}
            </Text>
          </Group>
          {canAuthor ? (
            <Textarea
              label={t("authoring.draft_label")}
              description={t("authoring.draft_desc")}
              autosize
              minRows={12}
              value={draft}
              onChange={(e) => setDraft(e.currentTarget.value)}
              styles={{ input: { fontFamily: "monospace" } }}
            />
          ) : (
            <Code block data-testid="rule-read-only">
              {JSON.stringify(rule, null, 2)}
            </Code>
          )}
          {canAuthor && parsed === null && (
            <Alert color="red">{t("authoring.draft_invalid_json")}</Alert>
          )}
          <Group>
            {canAuthor && (
              <Button
                disabled={parsed === null}
                loading={validation.isPending}
                onClick={() => {
                  if (parsed !== null) validation.mutate(parsed);
                }}
              >
                {t("authoring.validate")}
              </Button>
            )}
            <Button
              variant="light"
              disabled={parsed === null}
              loading={conflicts.isPending}
              onClick={() => {
                if (parsed !== null) conflicts.mutate(parsed);
              }}
            >
              {t("authoring.check_conflicts")}
            </Button>
            {canAuthor && !pinned && (
              <TextInput
                placeholder={t("authoring.org_placeholder")}
                aria-label={t("authoring.submit_org")}
                value={submitOrg}
                onChange={(e) => setSubmitOrgInput(e.currentTarget.value)}
              />
            )}
            {canAuthor && (
              <Button
                variant="outline"
                disabled={parsed === null || !isOrgNamespaced}
                loading={submission.isPending}
                onClick={() => {
                  if (parsed !== null) submission.mutate(parsed);
                }}
              >
                {t("authoring.submit_mr")}
              </Button>
            )}
            <Anchor
              component={Link}
              to={`/authoring/simulate?region=${encodeURIComponent(region)}`}
            >
              {t("authoring.open_simulator")}
            </Anchor>
          </Group>
          {canAuthor && submitOrg !== "" && !isOrgNamespaced && (
            <Text size="sm" c="dimmed">
              {t("authoring.org_namespace_hint")} ({submitOrg}:#…)
            </Text>
          )}
          {validation.data !== undefined && (
            <Alert
              color={validation.data.is_valid ? "green" : "red"}
              title={
                validation.data.is_valid
                  ? t("authoring.validate_ok")
                  : t("authoring.validate_failed")
              }
            >
              <List size="sm">
                {validation.data.errors.map((e) => (
                  <List.Item key={e}>{e}</List.Item>
                ))}
                {validation.data.warnings.map((w) => (
                  <List.Item key={w}>⚠ {w}</List.Item>
                ))}
              </List>
              {Object.keys(validation.data.resolved_symbols).length > 0 && (
                <Group mt="sm">
                  <Text size="sm">
                    {t("authoring.normalized_prefix")}{" "}
                    {Object.entries(validation.data.resolved_symbols)
                      .map(([typed, symbol]) => `${typed} → ${symbol}`)
                      .join(" · ")}
                  </Text>
                  {validation.data.normalized_rule !== null && (
                    <Button
                      size="xs"
                      variant="light"
                      onClick={() => {
                        setDraft(JSON.stringify(validation.data?.normalized_rule, null, 2));
                      }}
                    >
                      {t("authoring.apply_normalized")}
                    </Button>
                  )}
                </Group>
              )}
            </Alert>
          )}
          {validation.isError && (
            <Alert color="red" title={t("authoring.error_title")}>
              {validation.error.message}
            </Alert>
          )}
          {conflicts.data !== undefined && <ConflictsPanel data={conflicts.data} />}
          {conflicts.isError && (
            <Alert color="red" title={t("authoring.error_title")}>
              {conflicts.error.message}
            </Alert>
          )}
          {submission.data !== undefined && (
            <Alert color="green" title={t("authoring.mr_opened")}>
              <Anchor href={safeHttpUrl(submission.data.mr_url)} target="_blank" rel="noreferrer">
                {submission.data.mr_url}
              </Anchor>
            </Alert>
          )}
          {submission.isError && (
            <Alert color="red" title={t("authoring.error_title")}>
              {submission.error.message}
            </Alert>
          )}
        </Stack>
      )}
    </AppLayout>
  );
}

const KIND_COLOR: Record<RuleConflict["kind"], string> = {
  contradiction_risk: "red",
  duplicate: "orange",
  co_fire: "blue",
  same_insight_overlap: "gray",
};

function ConflictsPanel({ data }: { data: RuleConflictsResponse }): JSX.Element {
  if (data.conflicts.length === 0) {
    return (
      <Alert color="green" title={t("authoring.conflicts_title")}>
        {t("authoring.conflicts_none")}
        {data.warnings.map((w) => (
          <Text size="sm" key={w} mt="xs">
            ⚠ {w}
          </Text>
        ))}
      </Alert>
    );
  }
  // Any contradiction risk raises the whole panel to red; else advisory yellow.
  const hasRisk = data.conflicts.some((c) => c.kind === "contradiction_risk");
  return (
    <Alert color={hasRisk ? "red" : "yellow"} title={t("authoring.conflicts_title")}>
      <Text size="sm" mb="sm">
        {t("authoring.conflicts_advisory")}
      </Text>
      {data.warnings.map((w) => (
        <Text size="sm" key={w} mb="xs">
          ⚠ {w}
        </Text>
      ))}
      <Stack gap="sm">
        {data.conflicts.map((c) => (
          <div key={`${c.insight}:${c.conditions.join("|")}`}>
            <Group gap="xs">
              <Badge color={KIND_COLOR[c.kind]} variant="filled">
                {t(`authoring.conflict_kind_${c.kind}` as Parameters<typeof t>[0])}
              </Badge>
              {!c.certain && (
                <Badge color="gray" variant="outline">
                  {t("authoring.conflict_possible")}
                </Badge>
              )}
              <Text size="sm" fw={500}>
                {c.insight}
              </Text>
              <Text size="xs" c="dimmed">
                {c.type} · {t("authoring.conflict_shared")} {c.shared_biomarkers.join(", ")}
              </Text>
            </Group>
            <Text size="sm" mt={2}>
              {c.note}
            </Text>
            {c.conditions.length > 0 && (
              <Text size="xs" c="dimmed" style={{ fontFamily: "monospace" }}>
                {c.conditions.join(" AND ")}
              </Text>
            )}
          </div>
        ))}
      </Stack>
    </Alert>
  );
}
